// src/shared/components/Layout/BubbleNav.tsx
// ────────────────────────────────────────────────────────────────────────────
// BubbleNav — Mobile-only floating navigation bubble.
//
// Renders a small glass pill anchored top-left.  Tap → expands into a
// full-viewport dark-glass overlay with a hierarchical navigation tree
// covering every SPA page + sub-tab.  Replaces sidebar & inline tabs
// on screens < md (768 px).
//
// Design language: minimalistic dark glassmorphism, phone-native UX,
// smooth spring-like expand / collapse, WCAG-friendly touch targets.
// ────────────────────────────────────────────────────────────────────────────
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/shared/auth';
import { useEscapeLayer } from '@/shared/hooks/useEscapeLayer';
import { useTenantOverride } from '@/shared/auth/tenantContext';
import { Menu, X, ChevronRight, LogOut, Building2 } from 'lucide-react';
import {
  NAV_TREE,
  resolveNodePath,
  getBreadcrumb,
  filterTreeByCaps,
  type NavNode,
} from '@/shared/navigation/navTree';
import CreditBadge from '@/shared/components/telemetry/CreditBadge';

// ─── Recursive tree item ─────────────────────────────────────────────────────

interface NavItemProps {
  node: NavNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onNavigate: (node: NavNode) => void;
  pathname: string;
  searchParams: URLSearchParams;
}

function NavItem({
  node,
  depth,
  expanded,
  onToggle,
  onNavigate,
  pathname,
  searchParams,
}: NavItemProps) {
  const hasKids = !!node.children?.length;
  const isOpen = expanded.has(node.id);

  /** Leaf-level active check: path matches AND every declared param matches. */
  const isActive = useMemo(() => {
    const nodePath = resolveNodePath(node.id);
    if (pathname !== nodePath) return false;
    if (!node.params) return !hasKids;
    return Object.entries(node.params).every(
      ([k, v]) => searchParams.get(k) === v,
    );
  }, [node, pathname, searchParams, hasKids]);

  /** Is any descendant active? (used to accent parent rows) */
  const hasActiveChild = useMemo(() => {
    if (!hasKids) return false;
    const check = (nodes: NavNode[]): boolean =>
      nodes.some((n) => {
        const p = resolveNodePath(n.id);
        if (pathname !== p) return false;
        const ok =
          !n.params ||
          Object.entries(n.params).every(
            ([k, v]) => searchParams.get(k) === v,
          );
        if (ok && !n.children?.length) return true;
        return n.children ? check(n.children) : false;
      });
    return check(node.children!);
  }, [hasKids, node, pathname, searchParams]);

  const handleTap = useCallback(() => {
    if (hasKids) onToggle(node.id);
    else onNavigate(node);
  }, [hasKids, node, onToggle, onNavigate]);

  // Depth-based visual scaling
  const py = depth === 0 ? 'py-3' : depth === 1 ? 'py-2.5' : 'py-2';
  const textCls =
    depth === 0
      ? `text-[13px] font-semibold tracking-wide uppercase ${
          isActive || hasActiveChild ? 'text-neutral-200' : 'text-neutral-400'
        }`
      : depth === 1
        ? `text-[13px] font-medium ${
            isActive ? 'text-neon_purple-300' : 'text-neutral-400'
          }`
        : `text-xs ${isActive ? 'text-neon_purple-300' : 'text-neutral-500'}`;

  return (
    <>
      <button
        onClick={handleTap}
        className={`
          flex items-center gap-3 w-full text-left
          transition-colors duration-150 active:bg-white/[.04]
          touch-manipulation ${py}
        `}
        style={{ paddingLeft: `${20 + depth * 22}px`, paddingRight: 20 }}
      >
        {/* ── Indicator dot ── */}
        <span
          className={`
            w-[5px] h-[5px] rounded-full flex-shrink-0 transition-all duration-200
            ${
              isActive
                ? 'bg-neon_purple-400 shadow-[0_0_8px_rgba(176,1,244,0.5)]'
                : hasActiveChild
                  ? 'bg-neon_purple-700'
                  : hasKids
                    ? 'bg-neutral-700'
                    : 'bg-neutral-700/40'
            }
          `}
        />

        {/* ── Label ── */}
        <span className={`flex-1 truncate transition-colors ${textCls}`}>
          {node.label}
        </span>

        {/* ── Expand chevron ── */}
        {hasKids && (
          <ChevronRight
            size={12}
            className={`
              text-neutral-600 transition-transform duration-200
              ${isOpen ? 'rotate-90' : ''}
            `}
          />
        )}
      </button>

      {/* ── Collapsible children (grid-rows trick for smooth height) ── */}
      {hasKids && (
        <div
          className={`
            grid transition-[grid-template-rows] duration-300 ease-out
            ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
          `}
        >
          <div className="overflow-hidden">
            {node.children!.map((child) => (
              <NavItem
                key={child.id}
                node={child}
                depth={depth + 1}
                expanded={expanded}
                onToggle={onToggle}
                onNavigate={onNavigate}
                pathname={pathname}
                searchParams={searchParams}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Main BubbleNav ──────────────────────────────────────────────────────────

export default function BubbleNav({ forceMobile = false }: { forceMobile?: boolean }) {
  const [open, setOpen] = useState(false);
  // Root nodes start expanded so L1 items are immediately visible
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(NAV_TREE.map((n) => n.id)),
  );

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { logout, capabilities } = useAuth();
  const {
    isSuperadmin,
    tenants,
    activeTenantId,
    ownTenantId,
    isOverriding,
    setOverride,
  } = useTenantOverride();
  const [loggingOut, setLoggingOut] = useState(false);

  // ── Permission-filtered tree ──
  const visibleTree = useMemo(
    () => filterTreeByCaps(NAV_TREE, capabilities && typeof capabilities === 'object' ? capabilities : {}),
    [capabilities],
  );

  // ── Breadcrumb from current URL ──
  const breadcrumb = useMemo(
    () => getBreadcrumb(location.pathname, searchParams),
    [location.pathname, searchParams],
  );
  const crumbText = breadcrumb.length ? breadcrumb.join(' · ') : 'Navigate';

  // ── Auto-expand ancestry to current location when overlay opens ──
  useEffect(() => {
    if (!open) return;
    const toExpand = new Set<string>();
    const walk = (nodes: NavNode[], inheritedPath: string): boolean => {
      for (const n of nodes) {
        const p = n.path ?? inheritedPath;
        if (location.pathname !== p) continue;
        const ok =
          !n.params ||
          Object.entries(n.params).every(
            ([k, v]) => searchParams.get(k) === v,
          );
        if (!ok) continue;
        if (n.children) {
          toExpand.add(n.id);
          walk(n.children, p);
        }
        return true;
      }
      return false;
    };
    walk(NAV_TREE, '/');
    if (toExpand.size > 0) {
      setExpanded((prev) => {
        const next = new Set(prev);
        toExpand.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [open, location.pathname, searchParams]);

  // ── Lock body scroll when overlay is open ──
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const close = useCallback(() => setOpen(false), []);
  useEscapeLayer(close, open);

  const toggleExpand = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleNavigate = useCallback(
    (node: NavNode) => {
      const path = resolveNodePath(node.id);
      const sp = new URLSearchParams();
      if (node.params) {
        Object.entries(node.params).forEach(([k, v]) => sp.set(k, v));
      }
      const search = sp.toString();
      navigate(search ? `${path}?${search}` : path);
      close();
    },
    [navigate, close],
  );

  const handleLogout = useCallback(async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
      navigate('/login', { replace: true });
    }
  }, [logout, navigate]);

  return (
    <>
      {/* ════════════════════════════════════════════════════════════════════
          COLLAPSED BUBBLE — fixed glass pill, visible <md OR when forced
          ══════════════════════════════════════════════════════════════════ */}
      <button
        onClick={() => setOpen(true)}
        className={`
          fixed top-0 left-0 right-0 z-[60] ${forceMobile ? '' : 'md:hidden'}
          flex items-center gap-2 h-11 px-4
          pac-glass
          border-b border-neutral-800/50
          shadow-lg shadow-black/50
          text-neutral-400
          active:scale-[0.99]
          transition-all duration-200
          ${open ? 'opacity-0 scale-75 pointer-events-none' : 'opacity-100 scale-100'}
        `}
        aria-label="Open navigation"
      >
        <Menu size={15} className="flex-shrink-0" />
        <span className="text-[11px] font-medium tracking-wide truncate flex-1">
          {crumbText}
        </span>
        <CreditBadge showPct className="flex-shrink-0" />
        <ChevronRight size={12} className="text-neutral-600" />
      </button>

      {/* ════════════════════════════════════════════════════════════════════
          BACKDROP — dark blur overlay, tap to close
          ════════════════════════════════════════════════════════════════════ */}
      <div
        onClick={close}
        className={`
          fixed inset-0 z-[60] ${forceMobile ? '' : 'md:hidden'}
          bg-black/70 backdrop-blur-sm
          transition-opacity duration-300
          ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      />

      {/* ════════════════════════════════════════════════════════════════════
          EXPANDED PANEL — full-screen dark glass card, scale from top-left
          ════════════════════════════════════════════════════════════════════ */}
      <div
        className={`
          fixed top-2 left-2 right-2 bottom-2 z-[60] ${forceMobile ? '' : 'md:hidden'}
          flex flex-col
          pac-glass-raised
          border border-white/[.03]
          rounded-2xl
          shadow-2xl shadow-black/60
          origin-top-left
          transition-all duration-300 ease-[cubic-bezier(.32,.72,.32,1)]
          ${open
            ? 'scale-100 opacity-100'
            : 'scale-[0.15] opacity-0 pointer-events-none'
          }
        `}
      >
        {/* ── Header (tap anywhere to close) ── */}
        <div
          onClick={close}
          role="button"
          className="shrink-0 flex items-center justify-between px-5 pt-5 pb-3 cursor-pointer active:bg-white/[.03] transition-colors touch-manipulation"
        >
          <div className="min-w-0 flex-1 mr-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-600 select-none">
              Navigate
            </div>
            <div className="text-[11px] text-neutral-500 mt-0.5 truncate">
              {crumbText}
            </div>
          </div>
          <div
            className="
              w-8 h-8 flex items-center justify-center flex-shrink-0
              rounded-full bg-white/[.04] text-neutral-500
            "
            aria-label="Close navigation"
          >
            <X size={14} />
          </div>
        </div>

        {/* Gradient divider */}
        <div className="mx-5 h-px bg-gradient-to-r from-transparent via-neutral-800/60 to-transparent" />

        {/* ── Navigation tree ── */}
        <nav className="flex-1 min-h-0 overflow-y-auto py-2 overscroll-contain">
          {visibleTree.map((node) => (
            <NavItem
              key={node.id}
              node={node}
              depth={0}
              expanded={expanded}
              onToggle={toggleExpand}
              onNavigate={handleNavigate}
              pathname={location.pathname}
              searchParams={searchParams}
            />
          ))}
        </nav>

        {/* Gradient divider */}
        <div className="mx-5 h-px bg-gradient-to-r from-transparent via-neutral-800/60 to-transparent" />

        {/* ── Tenant selector (superadmin only) ── */}
        {isSuperadmin && (
          <div className="shrink-0 px-5 py-3">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-neutral-600 mb-2 select-none">
              <Building2 size={10} className="flex-shrink-0" />
              Tenant
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {/* Default (JWT) */}
              <button
                onClick={() => { setOverride(null); }}
                className={`
                  px-3 py-1.5 rounded-full text-[11px] font-medium
                  transition-colors touch-manipulation
                  ${!isOverriding
                    ? 'bg-neon_purple-900/30 text-neon_purple-300 border border-neon_purple-800/30'
                    : 'pac-glass text-neutral-500 border border-transparent active:border-neutral-500'
                  }
                `}
              >
                Default
              </button>

              {/* Per-tenant pills */}
              {tenants.map((t: any) => {
                const isActive = activeTenantId === t.idTenant;
                const isOwn = t.idTenant === ownTenantId;
                return (
                  <button
                    key={t.idTenant}
                    onClick={() => (isOwn ? setOverride(null) : setOverride(t.idTenant))}
                    className={`
                      px-3 py-1.5 rounded-full text-[11px] font-medium
                      transition-colors touch-manipulation
                      ${isActive
                        ? 'bg-neon_orange-950/30 text-neon_orange-300 border border-neon_orange-800/30'
                        : 'pac-glass text-neutral-500 border border-transparent active:border-neutral-500'
                      }
                    `}
                  >
                    {t.tenantName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Logout ── */}
        <div className="shrink-0 px-5 pb-5 pt-2">
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            className="
              flex items-center gap-2 w-full
              px-4 py-2.5 rounded-xl
              bg-neon_red-950/30 border border-neon_red-900/20
              text-neon_red-400 text-[12px] font-medium
              active:bg-neon_red-900/30
              disabled:opacity-50
              transition-colors touch-manipulation
            "
          >
            <LogOut size={13} />
            {loggingOut ? 'Logging out…' : 'Log out'}
          </button>
        </div>
      </div>
    </>
  );
}
